import { inject } from '@angular/core';
import { type CanActivateFn, Router, type UrlTree } from '@angular/router';
import { AuthStore } from '../auth/auth.store';
import { AuthService } from '../auth/auth.service';

/**
 * Guard for guest-only public pages (e.g. /welcome, /login, /).
 *
 * If the user has an active session or a valid refresh cookie, redirects them
 * to their destination shell (/empresa, /profissional, or /complete-cadastro)
 * instead of presenting auth screens.
 *
 * Returns true only when the user is genuinely unauthenticated.
 */
export const guestGuard: CanActivateFn = async (route): Promise<boolean | UrlTree> => {
  const authStore = inject(AuthStore);
  const authService = inject(AuthService);
  const router = inject(Router);

  const resolveRedirect = (): UrlTree | null => {
    if (!authStore.isAuthenticated()) return null;
    const user = authStore.user();
    if (!user?.role) {
      return router.parseUrl('/complete-cadastro');
    }
    const redirectParam = route?.queryParamMap?.get('redirect');
    if (
      redirectParam &&
      redirectParam.startsWith('/') &&
      !redirectParam.startsWith('//')
    ) {
      return router.parseUrl(redirectParam);
    }
    return router.parseUrl(authStore.shellPath());
  };

  const currentRedirect = resolveRedirect();
  if (currentRedirect) return currentRedirect;

  const refreshed = await authService.refresh();
  if (refreshed && authStore.isAuthenticated()) {
    const postRefreshRedirect = resolveRedirect();
    if (postRefreshRedirect) return postRefreshRedirect;
  }

  return true;
};
